import type { UserId } from "@/backend/db/drizzle/types/idTypes";
import type { UrlParamsDef } from "@/backend/db/drizzle/types/jsonTypes";
import { paginate } from "@/lib/utils";
import { isValidUrl } from "@/utils/validation/url";
import { formatDuration } from "date-fns";
import ms from "ms";
import { z } from "zod";

export const COOKIE_NAME_USER_ID = "merlin-survey-user-id";
export const COOKIE_NAME_SESSION_SECRET = "listen-survey-session-secret";
// Only exists in a partitioned jar, so its presence means the request comes from a cross-site iframe.
export const COOKIE_NAME_INTERVIEW_EMBEDDED = "listen-interview-embedded";
export const SESSION_MAX_AGE_SECONDS = 60 * 60 * 24 * 30; // 30 days
export const COOKIE_NAME_PKCE = "listen-pkce";
export const COOKIE_NAME_THEME = "listen-theme";

export type Theme = "light" | "dark";
export const parseThemeCookie = (value: string | undefined): Theme =>
  value === "dark" ? "dark" : "light";

export const COOKIE_NAME_UNIFIED_AGENT = "listen-unified-agent";
export const formatUnifiedAgentCookie = ({
  userId,
  isEnabled,
}: {
  userId: UserId;
  isEnabled: boolean;
}) => `${userId}:${isEnabled}`;
// Another account's value on a shared browser reads as unset.
export const parseUnifiedAgentCookie = ({
  value,
  userId,
}: {
  value: string | undefined;
  userId: UserId | undefined;
}): boolean | undefined => {
  if (!value || !userId) return undefined;
  if (value === formatUnifiedAgentCookie({ userId, isEnabled: true })) {
    return true;
  }
  if (value === formatUnifiedAgentCookie({ userId, isEnabled: false })) {
    return false;
  }
  return undefined;
};

// must be derivable from the interview URL (db not accessible when cookie is set)
export const getInterviewSessionCookieName = (linkId: string) =>
  `${COOKIE_NAME_SESSION_SECRET}-${linkId}`;

const VISITOR_MAX_AGE_SECONDS = 60 * 60 * 24 * 60; // 60 days

// Cross-site iframes only send SameSite=None cookies, and browsers that block third-party cookies only keep them when Partitioned.
const EMBEDDED_COOKIE_OPTIONS = {
  sameSite: "none" as const,
  secure: true,
  partitioned: true,
};

type InterviewCookieContext = { isEmbedded?: boolean };

// Shared by /s Interview cookie writes and /i middleware.
// TODO(future): set httpOnly once interview v1 (/s) is fully retired and no longer writes this cookie from the client.
export const getSurveyUserIdCookieOptions = ({
  isEmbedded = false,
}: InterviewCookieContext = {}) => ({
  sameSite: "lax" as const,
  secure: process.env.NODE_ENV === "production",
  path: "/",
  maxAge: VISITOR_MAX_AGE_SECONDS,
  ...(isEmbedded ? EMBEDDED_COOKIE_OPTIONS : {}),
});

// Outlives the cookies it describes so they are never rewritten as lax inside the iframe.
export const getInterviewEmbeddedCookieOptions = () => ({
  httpOnly: true,
  path: "/",
  maxAge: VISITOR_MAX_AGE_SECONDS,
  ...EMBEDDED_COOKIE_OPTIONS,
});

// Shared by middleware minting and restart cookie rotation so settings can't drift.
export const getInterviewSessionCookieOptions = ({
  isEmbedded = false,
}: InterviewCookieContext = {}) => ({
  httpOnly: true,
  // lax protects against most CSRF attacks: it prevents other websites
  // from making requests using this cookie
  sameSite: "lax" as const,
  // secure: true cookies are dropped by some browsers when not on HTTPS (i.e. for localhost)
  secure: process.env.NODE_ENV === "production",
  // path "/" so the cookie can be used for authentication on any api route
  path: "/",
  maxAge: SESSION_MAX_AGE_SECONDS,
  ...(isEmbedded ? EMBEDDED_COOKIE_OPTIONS : {}),
});

export const recordToArray = <K extends string | symbol, V>(
  record: Record<K, V>
): [K, V][] => Object.entries(record) as [K, V][];
export const recordToArrayNumberKeys = <V>(
  record: Record<number, V>
): [number, V][] =>
  Object.entries(record).map(([key, value]) => [Number(key), value]);

/** Indexes by key keeping the first row seen, so callers that pass rows in priority order (e.g. newest first) get their winner. */
export const firstByKey = <T>(
  rows: T[],
  key: (row: T) => string
): Map<string, T> =>
  rows.reduce(
    (byKey, row) => (byKey.has(key(row)) ? byKey : byKey.set(key(row), row)),
    new Map<string, T>()
  );

export const toURLSearchParams = (
  params: Record<string, string | string[] | undefined>
): URLSearchParams => {
  const result = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (Array.isArray(value)) {
      for (const v of value) {
        result.append(key, v);
      }
    } else if (value !== undefined) {
      result.set(key, value);
    }
  }
  return result;
};

export const getSiteOrigin = () => {
  const isPreview =
    process.env.NEXT_PUBLIC_VERCEL_ENV === "preview" ||
    process.env.VERCEL_ENV === "preview";

  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL;
  const vercelUrl = process.env.NEXT_PUBLIC_VERCEL_URL;
  // Workers/prod without SITE_URL must not fall back to localhost (reel callbacks,
  // Slack links). Local/dev keeps localhost so unset env still works offline.
  const localFallback = "http://localhost:3000";
  const prodFallback = "https://listenlabs.ai";
  const defaultFallback =
    process.env.NODE_ENV === "production" ? prodFallback : localFallback;
  let url = isPreview
    ? (vercelUrl ?? siteUrl ?? localFallback) // Prefer vercel url in preview environment
    : (siteUrl ?? vercelUrl ?? defaultFallback);
  url = url.startsWith("http") ? url : `https://${url}`;
  // No trailing slash. Prefer buildURL() for paths; use this when you need the origin.
  return url.replace(/\/$/, "");
};

const toOrigin = (value: string | undefined): string | null => {
  if (!value) return null;
  const withScheme = value.startsWith("http") ? value : `https://${value}`;
  try {
    return new URL(withScheme).origin;
  } catch {
    return null;
  }
};

/**
 * Vercel preview hostnames for this deployment: per-commit VERCEL_URL and the
 * stable VERCEL_BRANCH_URL. Empty outside preview (those env vars are unset).
 */
const getPreviewAuthOrigins = (): string[] => {
  const origins = [
    toOrigin(process.env.NEXT_PUBLIC_VERCEL_BRANCH_URL),
    toOrigin(process.env.NEXT_PUBLIC_VERCEL_URL),
  ].filter(notNull);
  return [...new Set(origins)];
};

const getConfiguredSiteOrigin = (): string | null =>
  toOrigin(process.env.NEXT_PUBLIC_SITE_URL);

/**
 * Extra origins from Infisical (NEXT_PUBLIC_SITE_URL_ALIASES, comma-separated)
 * that may share this deploy's auth cookies. US cutover sets
 * https://app.listenlabs.com while SITE_URL is still https://listenlabs.ai.
 * Preview hosts are not listed here; they come from getPreviewAuthOrigins().
 */
const getSiteUrlAliasOrigins = (): string[] => {
  const raw = process.env.NEXT_PUBLIC_SITE_URL_ALIASES;
  if (!raw) return [];
  const origins = raw
    .split(",")
    .map((part) => toOrigin(part.trim()))
    .filter(notNull);
  return [...new Set(origins)];
};

/**
 * Origin for auth `redirectTo` / SSO callbacks.
 *
 * getSiteOrigin() prefers the per-commit VERCEL_URL on preview, so opening the
 * branch alias stored the pkce code verifier in a cookie on the alias host while
 * the IdP was told to come back to the commit host. The verifier is not readable
 * there and sign-in dies with "code verifier not found". The same mismatch hits
 * production when SITE_URL is only one of the hosts this stack actually serves.
 *
 * Allowed hosts: configured SITE_URL / getSiteOrigin(), Infisical aliases, or
 * this deploy's preview URLs. Never trust Host as-is.
 */
export const resolveAuthOrigin = (host: string | null | undefined): string => {
  const requested = toOrigin(host?.trim() || undefined);
  return requested && getAllowedAppOrigins().includes(requested)
    ? requested
    : getSiteOrigin();
};

/** Every origin this deploy serves the app from. Never derived from the request. */
export const getAllowedAppOrigins = (): string[] => [
  ...new Set(
    [
      getSiteOrigin(),
      getConfiguredSiteOrigin(),
      ...getSiteUrlAliasOrigins(),
      ...getPreviewAuthOrigins(),
    ].filter(notNull)
  ),
];

export const isAllowedAppOrigin = (
  origin: string | null | undefined
): boolean => {
  const requested = toOrigin(origin?.trim() || undefined);
  return requested !== null && getAllowedAppOrigins().includes(requested);
};

/** Browser-side `resolveAuthOrigin`: keeps auth on whichever allowed host the user opened. */
export const getBrowserAuthOrigin = (): string =>
  typeof window === "undefined"
    ? getSiteOrigin()
    : resolveAuthOrigin(window.location.origin);

export type BuildUrlSearchParams =
  | Record<string, string | string[] | undefined>
  | URLSearchParams;

const buildUrlFromOrigin = ({
  origin,
  path,
  subdomain,
  searchParams,
  hash,
  helperName,
}: {
  origin: string;
  path: string;
  subdomain?: string;
  searchParams?: BuildUrlSearchParams;
  hash?: string;
  helperName: string;
}): string => {
  if (/^https?:\/\//i.test(path) || path.startsWith("//")) {
    throw new Error(
      `${helperName} path must be site-relative, not an absolute URL`
    );
  }
  if (subdomain !== undefined) {
    if (subdomain === "" || /[./]/.test(subdomain) || subdomain.includes(":")) {
      throw new Error(
        `${helperName} subdomain must be a single DNS label, got "${subdomain}"`
      );
    }
  }

  const base = new URL(origin);
  if (subdomain !== undefined) {
    base.hostname = `${subdomain}.${base.hostname}`;
  }

  const normalizedPath =
    path === "" ? "/" : path.startsWith("/") ? path : `/${path}`;
  // Resolve against origin so absolute paths replace pathname and relative
  // segments (and embedded ? / #) are handled by the URL constructor.
  const resolved = new URL(normalizedPath, `${base.origin}/`);
  resolved.protocol = base.protocol;
  resolved.username = base.username;
  resolved.password = base.password;
  resolved.host = base.host;

  if (searchParams !== undefined) {
    if (searchParams instanceof URLSearchParams) {
      for (const [key, value] of searchParams.entries()) {
        resolved.searchParams.append(key, value);
      }
    } else {
      for (const [key, value] of Object.entries(searchParams)) {
        if (value === undefined) {
          continue;
        }
        resolved.searchParams.delete(key);
        if (Array.isArray(value)) {
          for (const v of value) {
            resolved.searchParams.append(key, v);
          }
        } else {
          resolved.searchParams.set(key, value);
        }
      }
    }
  }

  if (hash !== undefined) {
    resolved.hash = hash.startsWith("#") ? hash : `#${hash}`;
  }

  // Match getSiteOrigin(): no trailing slash on bare origin.
  if (resolved.pathname === "/" && !resolved.search && !resolved.hash) {
    return resolved.toString().replace(/\/$/, "");
  }
  return resolved.toString();
};

// Absolute app URL via getSiteOrigin() + URL(). Optional subdomain → api.eu.listenlabs.com.
export const buildURL = ({
  path,
  subdomain,
  searchParams,
  hash,
}: {
  path: string;
  subdomain?: string;
  searchParams?: BuildUrlSearchParams;
  hash?: string;
}): string =>
  buildUrlFromOrigin({
    origin: getSiteOrigin(),
    path,
    subdomain,
    searchParams,
    hash,
    helperName: "buildURL",
  });

/**
 * buildURL for auth callbacks: same path handling, but the origin is the allowed
 * host the request/page is actually on (see resolveAuthOrigin).
 */
export const buildAuthURL = ({
  path,
  searchParams,
  origin,
}: {
  path: string;
  searchParams?: BuildUrlSearchParams;
  origin?: string;
}): string =>
  buildUrlFromOrigin({
    origin: origin ?? getBrowserAuthOrigin(),
    path,
    searchParams,
    helperName: "buildAuthURL",
  });

// Marketing website origin (apex). App shells use SITE_URL (future app./eu.).
export const getMarketingOrigin = (): string => {
  const marketingSiteUrl =
    process.env.NEXT_PUBLIC_MARKETING_SITE_URL ?? "https://listenlabs.com";
  const withScheme = marketingSiteUrl.startsWith("http")
    ? marketingSiteUrl
    : `https://${marketingSiteUrl}`;
  return withScheme.replace(/\/$/, "");
};

export const buildMarketingURL = ({
  path,
  searchParams,
  hash,
}: {
  path: string;
  searchParams?: BuildUrlSearchParams;
  hash?: string;
}): string =>
  buildUrlFromOrigin({
    origin: getMarketingOrigin(),
    path,
    searchParams,
    hash,
    helperName: "buildMarketingURL",
  });

const normalizePublicOrigin = ({
  value,
  fallback,
}: {
  value: string | undefined;
  fallback: string;
}): string => {
  const raw = value && value.length > 0 ? value : fallback;
  const withScheme = raw.startsWith("http") ? raw : `https://${raw}`;
  return withScheme.replace(/\/$/, "");
};

const buildPublicSiteURL = ({
  origin,
  path,
  searchParams,
  hash,
  helperName,
}: {
  origin: string;
  path: string;
  searchParams?: BuildUrlSearchParams;
  hash?: string;
  helperName: string;
}): string =>
  buildUrlFromOrigin({
    origin,
    path,
    searchParams,
    hash,
    helperName,
  });

export const getDocsOrigin = (): string =>
  normalizePublicOrigin({
    value: process.env.NEXT_PUBLIC_DOCS_SITE_URL,
    fallback: "https://docs.listenlabs.com",
  });

export const buildDocsURL = ({
  path,
  searchParams,
  hash,
}: {
  path: string;
  searchParams?: BuildUrlSearchParams;
  hash?: string;
}): string =>
  buildPublicSiteURL({
    origin: getDocsOrigin(),
    path,
    searchParams,
    hash,
    helperName: "buildDocsURL",
  });

export const getBlogOrigin = (): string =>
  normalizePublicOrigin({
    value: process.env.NEXT_PUBLIC_BLOG_SITE_URL,
    fallback: "https://blog.listenlabs.ai",
  });

export const buildBlogURL = ({
  path,
  searchParams,
  hash,
}: {
  path: string;
  searchParams?: BuildUrlSearchParams;
  hash?: string;
}): string =>
  buildPublicSiteURL({
    origin: getBlogOrigin(),
    path,
    searchParams,
    hash,
    helperName: "buildBlogURL",
  });

export const getTrustOrigin = (): string =>
  normalizePublicOrigin({
    value: process.env.NEXT_PUBLIC_TRUST_SITE_URL,
    fallback: "https://trust.listenlabs.ai",
  });

export const buildTrustURL = ({
  path,
  searchParams,
  hash,
}: {
  path: string;
  searchParams?: BuildUrlSearchParams;
  hash?: string;
}): string =>
  buildPublicSiteURL({
    origin: getTrustOrigin(),
    path,
    searchParams,
    hash,
    helperName: "buildTrustURL",
  });

// Supabase project URL from NEXT_PUBLIC_SUPABASE_URL (never derive from SITE_URL).
export const getSupabaseOrigin = (): string => {
  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
  if (!supabaseUrl) {
    throw new Error("NEXT_PUBLIC_SUPABASE_URL is not set");
  }
  const withScheme = supabaseUrl.startsWith("http")
    ? supabaseUrl
    : `https://${supabaseUrl}`;
  return withScheme.replace(/\/$/, "");
};

export const buildSupabaseURL = ({
  path,
  searchParams,
  hash,
}: {
  path: string;
  searchParams?: BuildUrlSearchParams;
  hash?: string;
}): string =>
  buildUrlFromOrigin({
    origin: getSupabaseOrigin(),
    path,
    searchParams,
    hash,
    helperName: "buildSupabaseURL",
  });

/** True for listenlabs.ai / .com and any subdomain (api., eu., app., …). */
export const isListenLabsHostname = (hostname: string): boolean => {
  const normalized = hostname.trim().toLowerCase();
  return (
    normalized === "listenlabs.ai" ||
    normalized === "listenlabs.com" ||
    normalized.endsWith(".listenlabs.ai") ||
    normalized.endsWith(".listenlabs.com")
  );
};

export const isListenLabsUrl = (url: string): boolean => {
  try {
    return isListenLabsHostname(new URL(url).hostname);
  } catch {
    return false;
  }
};

/**
 * Listen Labs hosts belonging to a non-production stack: the staging stack and
 * preview deployments. Everything else on a Listen Labs domain is production.
 */
export const isNonProdListenLabsHostname = (hostname: string): boolean => {
  const normalized = hostname.trim().toLowerCase();
  return (
    normalized === "staging.listenlabs.com" ||
    normalized.endsWith(".staging.listenlabs.com") ||
    normalized.endsWith(".preview.listenlabs.com")
  );
};

export const isNonProdListenLabsUrl = (url: string): boolean => {
  try {
    return isNonProdListenLabsHostname(new URL(url).hostname);
  } catch {
    return false;
  }
};

// Embed iframe proxy host for this stack.
// US SITE_URL listenlabs.ai → embed.listenlabs.ai
// EU SITE_URL eu.listenlabs.com → embed.eu.listenlabs.com
const isLocalOrPreviewHostname = (hostname: string): boolean =>
  hostname === "localhost" ||
  hostname.endsWith(".localhost") ||
  hostname.endsWith(".vercel.app");

export const getEmbedProxyHost = (): string => {
  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL ?? "https://listenlabs.ai";
  const withScheme = siteUrl.startsWith("http")
    ? siteUrl
    : `https://${siteUrl}`;
  let hostname: string;
  try {
    hostname = new URL(withScheme).hostname;
  } catch {
    return "embed.listenlabs.ai";
  }

  if (isLocalOrPreviewHostname(hostname)) {
    return "embed.listenlabs.ai";
  }
  return `embed.${hostname}`;
};

/** Figma allowlists real product origins; don't send localhost / preview hosts. */
export const getFigmaEmbedOrigin = (): string => {
  try {
    const url = getSiteOrigin();
    if (!isLocalOrPreviewHostname(new URL(url).hostname)) {
      return url;
    }
  } catch {
    // fall through
  }

  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL;
  if (siteUrl) {
    const withScheme = siteUrl.startsWith("http")
      ? siteUrl
      : `https://${siteUrl}`;
    try {
      const normalized = withScheme.replace(/\/$/, "");
      if (!isLocalOrPreviewHostname(new URL(normalized).hostname)) {
        return normalized;
      }
    } catch {
      // fall through
    }
  }

  return "https://listenlabs.ai";
};

// Any regional embed-proxy hostname (encoded target + embed.<site>).
// e.g. https-x.embed.listenlabs.ai, https-x.embed.eu.listenlabs.com
const EMBED_PROXY_HOSTNAME_RE =
  /^(?:[a-z0-9-]+\.)*embed\.(?:[a-z0-9-]+\.)*listenlabs\.(?:ai|com)$/i;

export const isEmbedProxyHostname = (hostname: string): boolean => {
  const normalized = hostname.trim().toLowerCase();
  return normalized.length > 0 && EMBED_PROXY_HOSTNAME_RE.test(normalized);
};

// Callers treat undefined as failure
export const normalizeEmbedProxyUrl = (
  url: string | null | undefined
): string | undefined => {
  if (typeof url !== "string") return undefined;
  const trimmed = url.trim();
  if (!trimmed || !isValidUrl(trimmed)) return undefined;
  try {
    return isEmbedProxyHostname(new URL(trimmed).hostname)
      ? trimmed
      : undefined;
  } catch {
    return undefined;
  }
};

export const isAllowedEmbedProxyUrl = (
  url: string | null | undefined
): boolean => normalizeEmbedProxyUrl(url) !== undefined;

// Callers treat undefined as failure
export const normalizeEmbedDisplayUrl = (
  url: string | null | undefined
): string | undefined => {
  if (typeof url !== "string") return undefined;
  const trimmed = url.trim();
  if (!trimmed) return undefined;
  if (isValidUrl(trimmed)) return trimmed;
  // if not http(s) accepted above, guard against ftp://, file:// etc.
  if (trimmed.includes("://")) return undefined;
  // Require a dot so single tokens like "TBD" are not prefixed into https://TBD
  if (!trimmed.includes(".")) return undefined;
  const prefixed = `https://${trimmed}`;
  return isValidUrl(prefixed) ? prefixed : undefined;
};

export const isAllowedEmbedDisplayUrl = (
  url: string | null | undefined
): boolean => normalizeEmbedDisplayUrl(url) !== undefined;

// Absolute deep-link to a response transcript. When messageIndex is provided,
// anchors to that exact message. Single producer of the /response/<id> shape.
export const getResponseLink = ({
  responseId,
  messageIndex,
}: {
  responseId: string;
  messageIndex?: number;
}): string => {
  if (messageIndex === undefined) {
    return buildURL({ path: `/response/${responseId}` });
  }
  return buildURL({
    path: `/response/${responseId}`,
    searchParams: { message: String(messageIndex) },
    hash: `a-${messageIndex}`,
  });
};

export const toDateTime = (secs: number) => {
  const t = new Date("1970-01-01T00:30:00Z"); // Unix epoch start.
  t.setSeconds(secs);
  return t;
};

export const pluralize = (word: string, count: number): string => {
  if (count === 1) {
    return word;
  }

  let pluralForm: string;

  // Handle words ending in 'y' preceded by a consonant
  if (
    word.endsWith("y") &&
    !["a", "e", "i", "o", "u"].includes(
      word.charAt(word.length - 2).toLowerCase()
    )
  ) {
    pluralForm = `${word.slice(0, -1)}ies`;
  }
  // Handle words ending in 's', 'x', 'z', 'ch', 'sh'
  else if (
    word.endsWith("s") ||
    word.endsWith("x") ||
    word.endsWith("z") ||
    word.endsWith("ch") ||
    word.endsWith("sh")
  ) {
    pluralForm = `${word}es`;
  }
  // Regular case: add 's'
  else {
    pluralForm = `${word}s`;
  }

  return pluralForm;
};

export const capitalize = (s: string): string =>
  s.charAt(0).toUpperCase() + s.slice(1);

export const capitalizeWords = (s: string) => {
  if (typeof s !== "string") return s;
  return s.split(" ").map(capitalize).join(" ");
};

export const timeAgo = (timestamp: Date, timeOnly?: boolean): string => {
  if (!timestamp) return "never";
  const elapsedMs = Date.now() - new Date(timestamp).getTime();
  if (elapsedMs < 60_000) return "just now";
  return `${ms(elapsedMs)}${timeOnly ? "" : " ago"}`;
};

export const formatDurationCompact = (
  duration: Duration,
  maxUnits: number = 2
) => {
  // for long times, it will be like "2 months 17 days 2h 52min 25s"
  // we want to show only the first 2 units
  const units = formatDuration(duration).split(" ");
  const str = units.slice(0, maxUnits * 2).join(" ");
  return str
    .replace(" seconds", "s")
    .replace(" second", "s")
    .replace(" minutes", "min")
    .replace(" minute", "min")
    .replace(" hours", "h")
    .replace(" hour", "h");
};

export function notNull<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

export function isNullish<T>(
  value: T | null | undefined
): value is null | undefined {
  return value === null || value === undefined;
}

/** Polyfill for Set.prototype.isDisjointFrom (Node 22+) */
export const isDisjointFrom = <T>(a: Set<T>, b: Set<T>): boolean => {
  for (const item of b) {
    if (a.has(item)) return false;
  }
  return true;
};

// logging error of a fetch response without throwing an error
export const logFetchError = async (res: Response, message?: string) => {
  const msg = message ?? "Fetch error";
  try {
    const content = await res.text();
    console.error(`${msg} ${res.status} ${res.statusText} ${content}`);
  } catch (e) {
    console.error(`${msg} ${res.status} ${res.statusText}`);
  }
};

// shuffles and returns a new array
export function shuffleArray<T>(array: T[], rng?: () => number): T[] {
  const gen = rng ?? Math.random;
  const a = [...array]; // don't mutate the input array
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(gen() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export async function parallelMapWithLoggingResponseTime<T, R>(
  array: T[],
  fn: (item: T, index: number) => Promise<R>,
  maxParallel: number
): Promise<R[]> {
  const durations: number[] = [];

  const wrappedFn = async (item: T, index: number): Promise<R> => {
    const startTime = Date.now();
    const result = await fn(item, index);
    durations.push(Date.now() - startTime);
    return result;
  };

  const results = await parallelMap(array, wrappedFn, maxParallel);

  const mean =
    durations.reduce((sum, curr) => sum + curr, 0) / durations.length;
  const variance =
    durations
      .map((x) => Math.pow(x - mean, 2))
      .reduce((sum, curr) => sum + curr, 0) / durations.length;
  const stdDev = Math.sqrt(variance);

  const sortedDurations = [...durations].sort((a, b) => a - b);
  const p75Index = Math.ceil(0.75 * sortedDurations.length) - 1;
  const p95Index = Math.ceil(0.95 * sortedDurations.length) - 1;
  const p75 = sortedDurations[p75Index];
  const p95 = sortedDurations[p95Index];

  console.log(
    `[ParallelMap] Mean: ${mean.toFixed(2)}ms, Std: ${stdDev.toFixed(2)}ms, Min: ${Math.min(...durations)}ms, p75: ${p75}ms, p95: ${p95}ms, Max: ${Math.max(...durations)}ms, Count: ${durations.length}`
  );

  return results;
}

/**
 * Like parallelMap, but returns early once a threshold percentage of items
 * complete, with a grace period for stragglers.
 *
 * @param completionThreshold - Fraction (0-1) of items that must complete before starting grace period (default: 0.95)
 * @param gracePeriodMs - Time to wait for remaining items after threshold is reached (default: 500)
 * @returns Results array where incomplete items are undefined
 */
export async function parallelMapWithEarlyReturn<T, R>(
  array: T[],
  fn: (item: T, index: number) => Promise<R>,
  maxParallel: number,
  {
    completionThreshold = 0.95,
    gracePeriodMs = 500,
    onProgress,
  }: {
    completionThreshold?: number;
    gracePeriodMs?: number;
    onProgress?: (completed: number, total: number) => void;
  } = {}
): Promise<(R | undefined)[]> {
  const total = array.length;
  if (total === 0) return [];

  const results = new Array<R | undefined>(total).fill(undefined);
  let completedCount = 0;
  let earlyReturnTriggered = false;
  let gracePeriodTimer: NodeJS.Timeout | null = null;

  let resolveEarlyReturn: () => void;
  const earlyReturnPromise = new Promise<void>((resolve) => {
    resolveEarlyReturn = resolve;
  });

  const queue: { item: T; index: number }[] = array.map((item, index) => ({
    item,
    index,
  }));

  const worker = async () => {
    while (!earlyReturnTriggered) {
      const next = queue.shift();
      if (!next) break;
      const { item, index } = next;

      const result = await fn(item, index);
      if (!earlyReturnTriggered) {
        results[index] = result;
        completedCount++;
        onProgress?.(completedCount, total);

        if (
          completedCount >= Math.ceil(total * completionThreshold) &&
          !gracePeriodTimer
        ) {
          gracePeriodTimer = setTimeout(() => {
            earlyReturnTriggered = true;
            resolveEarlyReturn();
          }, gracePeriodMs);
        }
      }
    }
  };

  const workers = new Array(maxParallel).fill(null).map(() => worker());
  const allWorkersPromise = Promise.all(workers);

  // Prevent unhandled rejection if workers fail after early return
  allWorkersPromise.catch(() => {});

  await Promise.race([allWorkersPromise, earlyReturnPromise]);

  if (gracePeriodTimer) {
    clearTimeout(gracePeriodTimer);
  }

  return results;
}

export async function parallelMap<T, R>(
  array: T[],
  fn: (item: T, index: number) => Promise<R>,
  maxParallel: number
): Promise<R[]> {
  const results = new Array<R>(array.length);
  const queue: { item: T; index: number }[] = array.map((item, index) => ({
    item,
    index,
  }));

  const worker = async () => {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const next = queue.shift();
      if (!next) break;
      const { item, index } = next;
      results[index] = await fn(item, index);
    }
  };

  const workers = new Array(maxParallel).fill(null).map(() => worker());
  await Promise.all(workers);

  return results;
}

export async function batchedParallelMap<T, R>(
  array: T[],
  batchFn: (batch: T[]) => Promise<R[]>,
  batchSize: number,
  maxParallel: number
): Promise<R[]> {
  const batches = paginate(array, batchSize);
  const resultBatches = await parallelMap(batches, batchFn, maxParallel);
  return resultBatches.flat();
}

/**
 * Processes an array in batches but sequentially, unlike batchedParallelMap which processes batches in parallel.
 */
export async function batchedMap<T, R>(
  array: T[],
  batchFn: (batch: T[]) => Promise<R[]>,
  batchSize: number
) {
  return batchedParallelMap(array, batchFn, batchSize, 1);
}

export function formatSQLList(list: string[]): string {
  return `(${list.map((item) => `"${item}"`).join(",")})`;
}

export function computeAverageData(list: number[]): {
  avg: number;
  cnt: number;
  sum: number;
} {
  return list.reduce(
    (acc, cur) => {
      acc.cnt += 1;
      acc.sum += cur;
      acc.avg = acc.sum / acc.cnt;
      return acc;
    },
    { avg: 0, cnt: 0, sum: 0 }
  );
}

export function computeAndSortByFrequencies<T>(
  list: T[]
): { value: T; frequency: number }[] {
  return Array.from(
    list.reduce(
      (acc, cur) => acc.set(cur, (acc.get(cur) || 0) + 1),
      new Map<T, number>()
    )
  )
    .map((a) => {
      return { value: a[0], frequency: a[1] };
    })
    .sort((a, b) => b.frequency - a.frequency);
}

export function getMedian(arr: number[]) {
  if (arr.length === 0) return 0;
  const sorted_arr = arr.sort((a, b) => a - b);
  const median_index = Math.floor(arr.length / 2);
  const median =
    arr.length % 2 === 0
      ? (sorted_arr[median_index - 1] + sorted_arr[median_index]) / 2
      : sorted_arr[median_index];
  return median;
}

type Comparable = string | number | boolean | Date;

/** sorts by first key, if equal, sorts by second key, etc. */
export function sortByKey<T>(
  array: T[],
  ...keys: ((item: T) => Comparable)[]
): T[] {
  return array.sort((a, b) => {
    for (const key of keys) {
      const valueA = key(a);
      const valueB = key(b);

      if (valueA < valueB) return -1;
      if (valueA > valueB) return 1;
    }
    return 0;
  });
}

export function uniqueByKey<T, K extends Comparable>(
  array: T[],
  key: (item: T) => K
): T[] {
  const seen = new Map<K, T>();
  return array.filter((item) => {
    const k = key(item);
    return seen.has(k) ? false : (seen.set(k, item), true);
  });
}

// node 22 has Set.prototype.symmetricDifference but we are not there yet
export function areSetsEqual<T>(a: Set<T>, b: Set<T>) {
  if (a.size !== b.size) return false;
  for (const item of Array.from(a.values())) {
    if (!b.has(item)) return false;
  }
  return true;
}

export function clamp(low: number, val: number, high: number) {
  return Math.max(low, Math.min(val, high));
}

/**
 * deletes fields from obj
 */
export function removeFields(obj: any, fields: string[]) {
  // Create a shallow copy of the object
  const result = { ...obj };
  for (const field of fields) {
    delete result[field];
  }
  return result;
}

/**
 * tries to parse a number range as outputted (for example) by gpt
 */
export const tryParseNumberRange = ({
  range,
  fallback_lower,
  fallback_upper,
}: {
  range: string[];
  fallback_lower: number;
  fallback_upper: number;
}) => {
  try {
    let lower = fallback_lower,
      upper = fallback_upper;
    if (range.length === 2) {
      // assuming that the input has the form ["X", "Y"]
      [lower, upper] = range.map((val) => parseInt(val)).sort((a, b) => a - b);
    } else if (range.length === 1) {
      // assuming that the input has the form ["X-Y"] or ["X+"]
      const value = range[0];
      if (value.includes("-")) {
        const last_hyphen_index = value.lastIndexOf("-");
        lower = parseInt(value.slice(0, last_hyphen_index));
        upper = parseInt(value.slice(last_hyphen_index + 1));
      } else if (value.includes("+")) {
        lower = parseInt(value.replace("+", ""));
      } else {
        throw new Error("Unknown format");
      }
    } else {
      throw new Error("Unknown format");
    }
    if (isNaN(lower) || isNaN(upper)) {
      throw new Error("Failed to parse at least one value");
    }
    if (lower > upper) {
      throw new Error("Invalid range");
    }
    return { lower, upper };
  } catch (err) {
    console.warn("failed to parse age range from input", range, err);
    return { lower: fallback_lower, upper: fallback_upper };
  }
};

/**
 * Converts an object of URL parameters into a URL query string
 * @param params Object containing URL parameters
 * @returns URL query string of the form `key1=value1&key2=value2`, empty string if no params
 */
export const params_to_url_string = (
  params: Record<string, string | number | boolean | UrlParamsDef[string]>
): string => {
  if (!params || Object.keys(params).length === 0) {
    return "";
  }
  const search_params = new URLSearchParams();
  recordToArray(params).forEach(([key, value]) => {
    if (key.trim() === "") {
      return;
    }
    if (typeof value === "object") {
      search_params.append(key.trim(), value.default ?? "");
    } else {
      search_params.append(key.trim(), value.toString().trim());
    }
  });
  return search_params.toString();
};

/**
 * Creates a deep clone of an object or array
 * @param obj The object or array to clone
 * @returns A deep copy of the input
 */
export const deepClone = <T>(obj: T): T => {
  if (obj === null || typeof obj !== "object") {
    return obj;
  }

  // Handle Date objects
  if (obj instanceof Date) {
    return new Date(obj.getTime()) as unknown as T;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => deepClone(item)) as unknown as T;
  }

  const clonedObj = {} as T;
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      clonedObj[key] = deepClone(obj[key]);
    }
  }

  return clonedObj;
};

export const mapRecordEntries = <K extends string, V, K2 extends string, V2>(
  record: Record<K, V>,
  fn: (entry: [K, V]) => [K2, V2]
): Record<K2, V2> =>
  Object.fromEntries(recordToArray(record).map(fn)) as Record<K2, V2>;

export const mapRecord = <K extends string, V, U>(
  record: Record<K, V>,
  fn: (key: K, value: V) => U
): Record<K, U> => {
  const result = {} as Record<K, U>;
  for (const key of Object.keys(record) as K[]) {
    result[key] = fn(key, record[key]);
  }
  return result;
};

export const mapNumericRecord = <V, U>(
  record: Record<number, V>,
  fn: (key: number, value: V) => U
): Record<number, U> => {
  const result = {} as Record<number, U>;
  for (const raw of Object.keys(record)) {
    const key = Number(raw);
    result[key] = fn(key, record[key]);
  }
  return result;
};

export const filterRecord = <K extends string, V>(
  record: Record<K, V>,
  fn: (key: K, value: V) => boolean
): Record<K, V> => {
  const result = {} as Record<K, V>;
  for (const key of Object.keys(record) as K[]) {
    const value = record[key];
    if (fn(key, value)) {
      result[key] = value;
    }
  }
  return result;
};

export const filterNumericRecord = <V>(
  record: Record<number, V>,
  fn: (key: number, value: V) => boolean
): Record<number, V> => {
  const result = {} as Record<number, V>;
  for (const raw of Object.keys(record)) {
    const key = Number(raw);
    const value = record[key];
    if (fn(key, value)) {
      result[key] = value;
    }
  }
  return result;
};

const TIME_GROUP_LABELS = [
  "Today",
  "Yesterday",
  "Past 7 days",
  "More than 7 days ago",
] as const;

export type TimeGroup<T> = { label: string; items: T[] };

export function groupByTimeRecency<T>(
  items: T[],
  getDate: (item: T) => Date
): TimeGroup<T>[] {
  const now = new Date();
  const todayMorning = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  );
  const yesterdayMorning = new Date(todayMorning);
  yesterdayMorning.setDate(yesterdayMorning.getDate() - 1);
  const sevenDaysAgo = new Date(todayMorning);
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

  const cutoffs = [todayMorning, yesterdayMorning, sevenDaysAgo];
  const groups: T[][] = TIME_GROUP_LABELS.map(() => []);

  const sorted = [...items].sort(
    (a, b) => getDate(b).getTime() - getDate(a).getTime()
  );

  for (const item of sorted) {
    const date = getDate(item);
    let placed = false;
    for (let i = 0; i < cutoffs.length; i++) {
      if (date >= cutoffs[i]) {
        groups[i].push(item);
        placed = true;
        break;
      }
    }
    if (!placed) groups[groups.length - 1].push(item);
  }

  return TIME_GROUP_LABELS.map((label, i) => ({
    label,
    items: groups[i],
  }));
}

const uuidSchema = z.string().uuid();

/** Zod schema for a branded uuid id; validates the uuid without casting. */
export const zodBrandedUuid = <TId extends string>() =>
  z.custom<TId>((value) => uuidSchema.safeParse(value).success);

/**
 * Rejects if `work` has not settled within `deadlineMs`. The losing side keeps
 * running to its own cleanup; this only stops it becoming an unhandled rejection.
 */
export const withDeadline = async <T>(
  work: Promise<T>,
  deadlineMs: number
): Promise<T> => {
  let timer: NodeJS.Timeout | undefined;
  try {
    return await Promise.race([
      work,
      new Promise<never>((_, reject) => {
        timer = setTimeout(
          () => reject(new Error(`Timed out after ${deadlineMs}ms`)),
          deadlineMs
        );
      }),
    ]);
  } finally {
    clearTimeout(timer);
    void work.catch(() => {});
  }
};
